import React, { FC, ReactElement, useRef, useState } from 'react'

import { BackToTopButton, JournalCardSearchEatable, JournalCardSearchMotion, SearchBar } from 'Components'
import TabSwitcher from 'Components/TabSwitcher/TabSwitcher'
import {
  FavoritesFood,
  FavoritesMapping,
  FavoritesMotionLevel,
  FavoritesRecipe,
  MealCategory,
  Motion,
  MotionLevelObject,
  QuantifiedEatable
} from 'Models'
import { FavoritesFilteredSectionList } from 'Pages/Favorites/FavoritesFilteredSectionList'
import { FavoriteTabNames, itemTypeNames } from 'Pages/Favorites/helpers/FavoriteTabNames'
import {
  getFavoriteEatableName,
  getFavoriteEatablesSectionList,
  getFavoriteMotionName,
  getFavoriteMotionsSectionList
} from 'Pages/Favorites/helpers/getFavoriteSectionList'
import { currentWeightSelector } from 'ReduxStore/bodyMeasurements'
import {
  favoriteSystemEatablesSelector,
  favoriteUserEatablesSelector,
  favoritesSelector
} from 'ReduxStore/favorites/favorites'
import { useAppDispatch, useAppSelector } from 'ReduxStore/hooks'
import { currentJournalDateSelector } from 'ReduxStore/journalDay'
import { postOrPatchJournalEatableRecordAction } from 'ReduxStore/journalEatableRecords'
import { addJournalMotionRecordAction } from 'ReduxStore/journalMotionRecords'

type Props = {
  onClose: () => void
}

const JournalFavorites: React.FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch()
  const [currentTabName, setCurrentTabName] = useState(FavoriteTabNames.FOODS)
  const [searchQuery, setSearchQuery] = useState('')
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const currentJournalDate = useAppSelector(currentJournalDateSelector)
  const currentWeight = useAppSelector(currentWeightSelector)

  const favoritesMotionLevels = useAppSelector(favoritesSelector<FavoritesMotionLevel>(FavoritesMapping.MOTIONS))
  const favoriteUserEatables = useAppSelector(favoriteUserEatablesSelector)

  const favoriteSystemFoods = useAppSelector(favoriteSystemEatablesSelector<FavoritesFood>(FavoritesMapping.FOODS))
  const favoriteSystemRecipes = useAppSelector(
    favoriteSystemEatablesSelector<FavoritesRecipe>(FavoritesMapping.RECIPES)
  )

  const Eatables: FC = () => {
    const renderCard = (favorite: FavoritesFood | FavoritesRecipe): ReactElement => {
      const onSave = (eatable: QuantifiedEatable, date: Date, mealCategory: MealCategory): void => {
        dispatch(postOrPatchJournalEatableRecordAction(eatable, mealCategory, date, 'search'))
        onClose()
      }

      return (
        <JournalCardSearchEatable
          key={favorite.id}
          quantifiedEatable={
            'food' in favorite
              ? {
                  foodId: favorite.food.id,
                  food: favorite.food,
                  quantity: favorite.quantity,
                  unit: favorite.unit
                }
              : {
                  recipeId: favorite.recipe.id,
                  recipe: favorite.recipe,
                  quantity: favorite.quantity,
                  unit: favorite.unit
                }
          }
          initialDate={currentJournalDate}
          onSave={onSave}
        />
      )
    }

    return (
      <FavoritesFilteredSectionList
        sectionList={getFavoriteEatablesSectionList({
          key: currentTabName,
          favoriteSystemFoods,
          favoriteSystemRecipes,
          favoriteUserEatables
        })}
        searchQuery={searchQuery}
        getName={getFavoriteEatableName}
        itemTypeName={itemTypeNames[currentTabName]}
        renderCard={renderCard}
      />
    )
  }

  const Motions: FC = () => {
    const onSave = (motion: Motion, duration: number, date: Date, category: MotionLevelObject | undefined): void => {
      dispatch(addJournalMotionRecordAction(motion, duration, (category || ({} as MotionLevelObject)).id, date))
      onClose()
    }

    const renderCard = (favorite: FavoritesMotionLevel): ReactElement => (
      <JournalCardSearchMotion
        key={favorite.id}
        motion={favorite.motionLevel.motion}
        initialDate={currentJournalDate}
        weight={currentWeight}
        onSave={onSave}
      />
    )

    return (
      <FavoritesFilteredSectionList
        sectionList={getFavoriteMotionsSectionList(favoritesMotionLevels)}
        searchQuery={searchQuery}
        getName={getFavoriteMotionName}
        itemTypeName={itemTypeNames[currentTabName]}
        renderCard={renderCard}
      />
    )
  }

  const tabs = [
    {
      name: FavoriteTabNames.FOODS,
      title: 'Nahrungsmittel',
      renderContent: <Eatables />
    },
    {
      name: FavoriteTabNames.RECIPES,
      title: 'Rezepte',
      renderContent: <Eatables />
    },
    {
      name: FavoriteTabNames.USER_EATABLES,
      title: 'Mein Essen',
      renderContent: <Eatables />
    },
    {
      name: FavoriteTabNames.MOTIONS,
      title: 'Aktivitäten',
      renderContent: <Motions />
    }
  ]

  const handleTabSwitch = (tabName: string): void => {
    setCurrentTabName(tabName as FavoriteTabNames)
    scrollContainerRef.current?.scrollTo({ top: 0 })
  }

  return (
    <>
      <div className="journal-add-modal-toolbar">
        <SearchBar value={searchQuery} onChangeText={setSearchQuery} labelText="In Favoriten suchen …" />

        <div className="mt-2">
          <TabSwitcher part="tabs" activeTab={currentTabName} onTabSwitch={handleTabSwitch} tabs={tabs} />
        </div>
      </div>

      <div className="journal-add-modal-hits">
        <div ref={scrollContainerRef} className="toolbar-modal-body custom-scrollbar">
          <TabSwitcher part="content" activeTab={currentTabName} tabs={tabs} />
        </div>

        <BackToTopButton scrollContainerRef={scrollContainerRef} />
      </div>
    </>
  )
}

export default JournalFavorites
